import React, { useEffect, useState, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface CheckboxDropdownProps {
  label: string;
  options: string[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
  singleSelect?: boolean;
  hideCount?: boolean;
  isActive?: boolean;
  shiftMenuRight?: boolean;
}

export const CheckboxDropdown: React.FC<CheckboxDropdownProps> = ({ label, options, selected, onChange, singleSelect = false, hideCount = false, isActive, shiftMenuRight = false }) => {
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inactiveText = isDark ? 'text-rs-off-white/70' : 'text-black';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allSelected = options.length > 0 && selected.size === options.length;
  const isFiltered = selected.size < options.length;
  const searchLower = search.toLowerCase();
  const matchesSearch = (o: string) => o != null && o.toLowerCase().includes(searchLower);
  const selectedFiltered = !allSelected ? options.filter(o => selected.has(o) && matchesSearch(o)) : [];
  const unselectedFiltered = options.filter(o => !selected.has(o) && matchesSearch(o));
  const filtered = allSelected ? options.filter(matchesSearch) : [];

  const toggleAll = () => {
    onChange(allSelected ? new Set() : new Set(options));
  };

  const toggle = (opt: string) => {
    if (singleSelect) {
      onChange(new Set([opt]));
      setOpen(false);
    } else {
      const next = new Set(selected);
      if (next.has(opt)) next.delete(opt);
      else next.add(opt);
      onChange(next);
    }
  };

  const displayLabel = hideCount && selected.size > 0
    ? Array.from(selected)[0]
    : allSelected ? label : `${label} (${selected.size})`;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(p => !p)}
        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors border flex items-center gap-1 ${
          isActive !== undefined
            ? isActive
              ? 'bg-rs-green text-black border-rs-green'
              : `bg-rs-jet ${inactiveText} hover:bg-rs-jet-light border-rs-jet-light`
            : isFiltered
              ? 'bg-rs-green text-black border-rs-green'
              : `bg-rs-jet ${inactiveText} hover:bg-rs-jet-light border-rs-jet-light`
        }`}
      >
        {displayLabel}
        <span className="text-xs opacity-60">▼</span>
      </button>
      {open && (
        <div className={`absolute top-full mt-1 w-56 bg-rs-jet border border-rs-jet-light rounded-lg shadow-xl z-50 ${shiftMenuRight ? '-right-40' : 'right-0'}`}>
          <div className="p-2">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="bg-rs-off-black border border-rs-jet-light text-rs-off-white placeholder-rs-off-white/40 rounded px-2 py-1 text-sm w-full outline-none"
            />
          </div>
          {!singleSelect && (
            <div className="px-2 pb-1 border-b border-rs-jet-light">
              <label className="flex items-center gap-2 cursor-pointer text-rs-off-white hover:bg-rs-jet-light px-1 py-1 rounded text-sm select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="rounded"
                />
                Select All
              </label>
            </div>
          )}
          <div className="max-h-64 overflow-y-auto p-1">
            {allSelected ? (
              filtered.map(opt => (
                <label key={opt} className="flex items-center gap-2 cursor-pointer text-rs-off-white hover:bg-rs-jet-light px-2 py-1 rounded text-sm select-none">
                  <input type="checkbox" checked onChange={() => toggle(opt)} />
                  {opt}
                </label>
              ))
            ) : (
              <>
                {selectedFiltered.length > 0 && (
                  <>
                    <p className="px-2 pt-1 pb-0.5 text-xs text-rs-off-white/40 uppercase tracking-wider">Selected</p>
                    {selectedFiltered.map(opt => (
                      <label key={opt} className="flex items-center gap-2 cursor-pointer text-rs-off-white hover:bg-rs-jet-light px-2 py-1 rounded text-sm select-none">
                        <input type="checkbox" checked onChange={() => toggle(opt)} />
                        {opt}
                      </label>
                    ))}
                    {unselectedFiltered.length > 0 && <div className="my-1 border-t border-rs-jet-light" />}
                  </>
                )}
                {unselectedFiltered.map(opt => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer text-rs-off-white/60 hover:bg-rs-jet-light hover:text-rs-off-white px-2 py-1 rounded text-sm select-none">
                    <input type="checkbox" checked={false} onChange={() => toggle(opt)} />
                    {opt}
                  </label>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
